import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useLoyaltyCard, useSetManualDiscount } from './api';

/**
 * Sekcja karty lojalnościowej w detalu klienta (Faza 15).
 *
 * Dostęp:
 *   - ADMIN i RECEPTION widzą kartę (RBAC backend),
 *   - tylko ADMIN może edytować `manualDiscountPercent` (bonus / podłoga zniżki).
 *
 * UX:
 *   - 4 kafle KPI: liczba wizyt, łącznie wydane, aktualny tier, efektywna zniżka,
 *   - pasek postępu do następnego progu (gdy `nextTier != null`),
 *   - sekcja edycji „Bonus admina" pokazywana tylko ADMINOWI.
 */
export function LoyaltyCardSection({ clientId }: { clientId: number }): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const canView = isAdmin || user?.role === 'RECEPTION';

  const { data, isLoading, error } = useLoyaltyCard(clientId, canView);
  const mutation = useSetManualDiscount(clientId);

  const [manualInput, setManualInput] = useState<string>('');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data) setManualInput(String(data.manualDiscountPercent));
  }, [data?.manualDiscountPercent]);

  if (!canView) return null;
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 text-sm text-zinc-400">
        {t('common.loading')}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 text-sm text-red-600">
        {error ? extractApiError(error) : t('loyalty.errorLoading')}
      </div>
    );
  }

  const tierLabel =
    data.automaticTier === 2
      ? t('loyalty.tier.vip')
      : data.automaticTier === 1
        ? t('loyalty.tier.regular')
        : t('loyalty.tier.none');

  // Pasek postępu: ile wizyt do następnego progu. Skala względem kroku progu
  // (np. po 5 z 10 → 50%, po 15 z 20 → 50% w drugiej ścieżce). Gdy max tier — 100%.
  const progressDenominator =
    data.nextTier === null
      ? 1
      : data.nextTier.tier === 1
        ? data.config.tier1.visits
        : data.config.tier2.visits - data.config.tier1.visits;
  const progressValue =
    data.nextTier === null
      ? progressDenominator
      : data.nextTier.tier === 1
        ? data.visitsCount
        : data.visitsCount - data.config.tier1.visits;
  const progressPct = Math.min(100, Math.round((progressValue / progressDenominator) * 100));

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavedMsg(null);
    setErrMsg(null);
    const value = Number.parseInt(manualInput, 10);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      setErrMsg(t('loyalty.manualInvalid'));
      return;
    }
    try {
      await mutation.mutateAsync({ manualDiscountPercent: value });
      setSavedMsg(t('loyalty.manualSaved'));
    } catch (err) {
      setErrMsg(extractApiError(err));
    }
  };

  return (
    <section className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200">
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-zinc-700">{t('loyalty.title')}</h2>
        <span className="text-xs text-zinc-400">
          {t('loyalty.thresholds', {
            v1: data.config.tier1.visits,
            p1: data.config.tier1.percent,
            v2: data.config.tier2.visits,
            p2: data.config.tier2.percent,
          })}
        </span>
      </header>

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <Kpi label={t('loyalty.visits')} value={String(data.visitsCount)} />
        <Kpi
          label={t('loyalty.totalSpent')}
          value={`${data.totalSpent} zł`}
        />
        <Kpi label={t('loyalty.tierLabel')} value={tierLabel} highlight={data.automaticTier > 0} />
        <Kpi
          label={t('loyalty.effectiveDiscount')}
          value={`${data.effectivePercent}%`}
          highlight={data.effectivePercent > 0}
        />
      </div>

      {data.nextTier !== null && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-zinc-500">
            <span>
              {t('loyalty.toNextTier', {
                tier: data.nextTier.tier,
                remaining: data.nextTier.visitsRemaining,
                percent: data.nextTier.percent,
              })}
            </span>
            <span className="tabular-nums">{progressPct}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-zinc-100">
            <div
              className="h-full bg-brand-500 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {isAdmin && (
        <form onSubmit={(e) => void onSave(e)} className="mt-5 border-t border-zinc-100 pt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            {t('loyalty.manualSectionTitle')}
          </h3>
          <p className="mt-1 text-xs text-zinc-500">{t('loyalty.manualSectionHint')}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="text-sm text-zinc-700">
              {t('loyalty.manualField')}
            </label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                className="input w-24"
              />
              <span className="text-sm text-zinc-500">%</span>
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t('common.saving') : t('loyalty.manualSave')}
            </button>
            {data.manualDiscountPercent > 0 && (
              <button
                type="button"
                className="btn-secondary"
                disabled={mutation.isPending}
                onClick={() => {
                  setManualInput('0');
                  void mutation.mutateAsync({ manualDiscountPercent: 0 }).then(
                    () => setSavedMsg(t('loyalty.manualCleared')),
                    (err) => setErrMsg(extractApiError(err)),
                  );
                }}
              >
                {t('loyalty.manualClear')}
              </button>
            )}
          </div>
          {savedMsg && (
            <div className="mt-2 text-xs text-emerald-700">{savedMsg}</div>
          )}
          {errMsg && (
            <div className="mt-2 text-xs text-red-600">{errMsg}</div>
          )}
        </form>
      )}
    </section>
  );
}

function Kpi({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): ReactNode {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        highlight ? 'border-brand-200 bg-brand-50/60' : 'border-zinc-200 bg-zinc-50/40'
      }`}
    >
      <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">
        {label}
      </div>
      <div
        className={`mt-1 text-lg font-semibold tabular-nums ${
          highlight ? 'text-brand-700' : 'text-zinc-800'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
